import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import 'antd/dist/reset.css';
import apiClient, { loginAPI } from './utils/api';
import Home from './pages/Home';
import LoginModal from './components/UI/LoginModal';
import './App.css';

// 用户信息接口
interface UserInfo {
  username: string;
  storeUserName?: string;
  storeUserId?: number;
  realName?: string;
  isSuper?: number;
}

// App 内容组件，使用 useNavigate 和 useLocation hooks
const AppContent: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loginType, setLoginType] = useState<'merchant' | 'company'>('merchant');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

    // 获取后台登录用户信息并同步到前端
    const syncStoreUserInfo = async () => {
      try {
        const data = await loginAPI.getStoreUserInfo();
  
        if (data.code === 1 && data.data && data.data.is_logged_in) {
          const storeInfo = data.data.store_user_info;
  
          // 更新 userInfo 状态
          const userInfoData = {
            username: storeInfo.user_name,
            storeUserName: storeInfo.user_name,
            storeUserId: storeInfo.store_user_id,
            realName: storeInfo.real_name,
            isSuper: storeInfo.is_super
          };
  
          setUserInfo(userInfoData);
  
          // 同步到 localStorage
          localStorage.setItem('userInfo', JSON.stringify(userInfoData));
  
          console.log('同步后台登录信息成功:', storeInfo);
        } else {
          // 后台未登录，直接清除本地登录状态
          console.log('后台未登录，清除前端登录状态');
          localStorage.removeItem('userInfo');
          setUserInfo(null);
        }
      } catch (err) {
        console.error('获取后台登录信息失败:', err);
        // API调用失败，视为未登录，清除本地状态
        localStorage.removeItem('userInfo');
        setUserInfo(null);
      }
    };
  // 检查 URL 参数中的 logout 标志
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const logoutFlag = params.get('logout');
    const accountSwitchedFlag = params.get('account_switched');

    if (logoutFlag === 'true') {
      // 后端退出登录后跳转到这里，清除本地登录状态
      console.log('检测到退出登录标志，清除本地登录状态');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('logout_timestamp');
      setUserInfo(null);

      // 重定向到首页（移除 URL 参数）
      navigate('/', { replace: true });
    } else if (accountSwitchedFlag === '1') {
      // 账号切换后，同步后台登录信息
      console.log('检测到账号切换，同步登录信息');
      syncStoreUserInfo();
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  // 初始化时检查本地存储的登录状态
  useEffect(() => {
    const storedUser = localStorage.getItem('userInfo');
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    }

    // 页面加载时检查后台登录状态并同步
    syncStoreUserInfo();
  }, []);

  // 页面加载时检查登录状态
  useEffect(() => {
    const checkLoginOnLoad = async () => {
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        return; // 没有存储的用户信息，无需检查
      }

      try {
        // 调用后台登录状态检查接口验证登录状态
        const data = await loginAPI.getStoreUserInfo();
        // 如果后台返回未登录，清除本地状态
        if (!data.data || !data.data.is_logged_in) {
          console.log('后台登录状态已失效，清除本地登录信息');
          localStorage.removeItem('userInfo');
          setUserInfo(null);
        }
      } catch (err) {
        // 请求失败，可能已退出登录，清除本地状态
        console.log('登录状态检查失败，清除登录信息', err);
        localStorage.removeItem('userInfo');
        setUserInfo(null);
      }
    };

    checkLoginOnLoad();
  }, []);

  const showModal = (type: 'merchant' | 'company') => {
    setLoginType(type);
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
  };

  // 登录成功回调
  const handleLoginSuccess = (userData: UserInfo) => {
    setUserInfo(userData);
    localStorage.setItem('userInfo', JSON.stringify(userData));
    setIsModalVisible(false);
  };

  // 退出登录
  const handleLogout = async () => {
    try {
      await apiClient.post('/index.php?s=/store/passport/logout');
      // 清除本地存储
      localStorage.removeItem('userInfo');
      setUserInfo(null);
    } catch (err) {
      console.error('退出登录失败:', err);
    }
  };

  // 检查登录状态（当页面获得焦点时调用）
  const checkLoginStatus = async () => {
    const storedUser = localStorage.getItem('userInfo');
    if (!storedUser) {
      return; // 没有存储的用户信息，无需检查
    }

    try {
      // 调用后台登录状态检查接口验证登录状态
      const data = await loginAPI.getStoreUserInfo();
      // 如果后台返回未登录，清除本地状态
      if (!data.data || !data.data.is_logged_in) {
        console.log('后台登录状态已失效，清除本地登录信息');
        localStorage.removeItem('userInfo');
        setUserInfo(null);
      }
    } catch (err) {
      // 请求失败，可能已退出登录，清除本地状态
      console.log('登录状态检查失败，清除登录信息', err);
      localStorage.removeItem('userInfo');
      setUserInfo(null);
    }
  };

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面重新获得焦点时检查登录状态
        checkLoginStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 定时检查登录状态（每5分钟）
  useEffect(() => {
    const interval = setInterval(checkLoginStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 监听 localStorage 变化（其他窗口退出时触发）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // 检测到退出标志时清除登录信息
      if (e.key === 'logout_timestamp' && e.newValue) {
        console.log('检测到后台退出，清除登录信息');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('logout_timestamp');
        setUserInfo(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            <Home
              onOpenLoginModal={showModal}
              userInfo={userInfo}
              onLogout={handleLogout}
            />
          }
        />
        <Route
          path="/companyinfo"
          element={
            <Home
              onOpenLoginModal={showModal}
              userInfo={userInfo}
              onLogout={handleLogout}
            />
          }
        />
        {/* 其他路由都指向 Home，内容通过 tab 切换 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <LoginModal
        visible={isModalVisible}
        onClose={hideModal}
        loginType={loginType}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <Router>
        <AppContent />
      </Router>
    </ConfigProvider>
  );
}

export default App;
